import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser } from "lucide-react";
import { api } from "../../../lib/api";
import { cacheGet, cacheSet } from "../../../lib/offlineCache";
import { Button } from "../../../components/ui/button";
import { StepProgress } from "../../../components/kiosk/StepProgress";

export default function BriefingStep({ visitId, offline = false, onNext }) {
  const [pending, setPending] = useState(null);
  const [index, setIndex] = useState(0);
  const [collected, setCollected] = useState([]);
  const sigRef = useRef(null);

  useEffect(() => {
    if (offline) {
      setPending(cacheGet("active_briefings"));
    } else {
      api.get(`/visits/${visitId}/pending-briefings`).then(({ data }) => {
        setPending(data);
        cacheSet("active_briefings", data);
      });
    }
  }, [visitId, offline]);

  useEffect(() => {
    if (pending !== null && !pending.length) onNext(offline ? [] : undefined);
  }, [pending, offline, onNext]);

  if (pending === null || !pending.length)
    return (
      <div className="text-center text-slate-400 py-12" data-testid="briefing-loading">
        Loading briefing...
      </div>
    );

  const briefing = pending[index];

  const submitSignature = async () => {
    if (sigRef.current?.isEmpty()) return;
    const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
    if (offline) {
      const next = [...collected, { briefing_id: briefing.id, signature_image_base64: dataUrl }];
      if (index + 1 < pending.length) {
        setCollected(next);
        setIndex(index + 1);
        sigRef.current?.clear();
      } else {
        onNext(next);
      }
      return;
    }
    await api.post(`/visits/${visitId}/sign-briefing`, { briefing_id: briefing.id, signature_image_base64: dataUrl });
    if (index + 1 < pending.length) {
      setIndex(index + 1);
      sigRef.current?.clear();
    } else {
      onNext();
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="checkin-briefing-step">
      <StepProgress current="briefing" />
      <div className="bg-white border border-slate-200 shadow-sm rounded-lg overflow-hidden" data-testid="briefing-card">
        <div className="bg-brand-navy p-6 sm:p-8 relative">
          {pending.length > 1 && (
            <span
              className="absolute top-6 right-6 sm:right-8 text-xs font-bold text-brand-cyan bg-white/10 rounded-full px-2.5 py-1"
              data-testid="briefing-progress-badge"
            >
              {index + 1}/{pending.length}
            </span>
          )}
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-cyan mb-2" data-testid="briefing-eyebrow">
            Please Read Before Entry
          </p>
          <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white" data-testid="briefing-title">
            {briefing.title}
          </h2>
          <p className="text-sm text-slate-300 mt-1" data-testid="briefing-subtitle">
            Ray International / Radish Technologies — Factory Premises
          </p>
        </div>
        <div className="p-6 sm:p-8 space-y-5">
          <div
            className="briefing-content max-h-80 overflow-y-auto pr-1"
            data-testid="briefing-content"
            dangerouslySetInnerHTML={{ __html: briefing.content_html }}
          />
          {briefing.video_url && (
            <a href={briefing.video_url} target="_blank" rel="noopener noreferrer" data-testid="briefing-video-link" className="text-brand-cyan text-sm underline block">
              Watch briefing video
            </a>
          )}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-slate-400">Sign below to acknowledge</p>
            <div className="border-2 border-sky-200 bg-white rounded-md" data-testid="signature-pad-container">
              <SignatureCanvas ref={sigRef} penColor="black" canvasProps={{ className: "w-full h-40" }} />
            </div>
            <button onClick={() => sigRef.current?.clear()} data-testid="clear-signature-button" className="flex items-center gap-2 text-sm text-slate-400 hover:text-brand-navy">
              <Eraser size={14} /> Clear
            </button>
          </div>
          <Button onClick={submitSignature} data-testid="submit-signature-button" className="w-full h-16 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-lg">
            Sign &amp; Continue ({index + 1}/{pending.length})
          </Button>
        </div>
      </div>
    </div>
  );
}
